import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { RootLayout } from './shell/AppShell';
import { NotFound } from './shell/NotFound';
import { LoadingArea } from '@/components/ui';
import { validateAdminSearch } from '@/features/admin/state';

/**
 * Every route is a real, bookmarkable URL (§3). Pages are code-split per route; search params are
 * validated here so links and navigation are type-checked against them.
 */

const str = (v: unknown) => (typeof v === 'string' && v !== '' ? v : undefined);
const oneOf = <T extends string>(v: unknown, allowed: readonly T[]): T | undefined =>
  typeof v === 'string' && (allowed as readonly string[]).includes(v) ? (v as T) : undefined;
/** Marks every validated search param optional, so links may omit them. */
const optional =
  <T extends object>(fn: (s: Record<string, unknown>) => T) =>
  (s: Record<string, unknown>): Partial<T> =>
    fn(s);

const rootRoute = createRootRoute({ component: RootLayout, notFoundComponent: NotFound });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/today' });
  },
});

const page = (loader: () => Promise<Record<string, unknown>>, name: string) =>
  lazyRouteComponent(loader as never, name as never);

const todayRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/today',
  validateSearch: optional((s) => ({ view: oneOf(s.view, ['table', 'split'] as const), sel: str(s.sel), strategy: str(s.strategy) })),
  component: page(() => import('@/features/today/TodayPage'), 'TodayPage'),
});

// The Scanner page was folded into Today (web_app_design.md §18): its table is Today's with the
// optional market columns, its strategy picker is Today's "By strategy", and its timeline is on the
// strategy's own page. Old links land on Today with the strategy they named.
const scannerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/scanner',
  beforeLoad: ({ search }) => {
    const strategy = str((search as Record<string, unknown>).strategy);
    throw redirect({ to: '/today', search: strategy ? { strategy } : {} });
  },
});

const strategiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/strategies',
  component: page(() => import('@/features/strategies/StrategiesPage'), 'StrategiesPage'),
});

const strategyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/strategies/$strategyId',
  component: page(() => import('@/features/strategies/StrategiesPage'), 'StrategiesPage'),
});

const newStrategyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/strategies/new',
  component: page(() => import('@/features/builder/BuilderPage'), 'NewStrategyPage'),
});

const editStrategyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/strategies/$strategyId/edit',
  component: page(() => import('@/features/builder/BuilderPage'), 'EditStrategyPage'),
});

const templatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/templates',
  component: page(() => import('@/features/templates/TemplatesPage'), 'TemplatesPage'),
});

const watchlistsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/watchlists',
  component: page(() => import('@/features/watchlists/WatchlistsPage'), 'WatchlistsPage'),
});

const watchlistRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/watchlists/$watchlistId',
  component: page(() => import('@/features/watchlists/WatchlistsPage'), 'WatchlistsPage'),
});

const alertsSearch = optional((s) => ({
  status: oneOf(s.status, ['sent', 'suppressed', 'failed'] as const),
  unread: s.unread === true || s.unread === 'true' ? true : undefined,
}));

const alertsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/alerts',
  validateSearch: alertsSearch,
  component: page(() => import('@/features/alerts/AlertsPage'), 'AlertsPage'),
});

const alertRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/alerts/$alertId',
  validateSearch: alertsSearch,
  component: page(() => import('@/features/alerts/AlertsPage'), 'AlertsPage'),
});

// The economic calendar and recent earnings (docs/news_design.md §8). Every plan.
const calendarRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/calendar',
  component: page(() => import('@/features/calendar/CalendarPage'), 'CalendarPage'),
});

// Markets analytics (docs/markets_design.md §12.1): the sub-tab is in the URL, so a tab is linkable
// and the back button works; `ids`/`range` carry Relative strength's "Open in Performance", and
// `instrument`/`kind` the Calendar's link to a release's past reactions on Events (§6.13). `earnings`
// is the Events tab's old name, still accepted.
const marketsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/markets',
  validateSearch: optional((s) => ({
    tab: oneOf(s.tab, ['performance', 'asset-profile', 'volatility-cone', 'trend', 'correlation', 'regimes', 'events', 'earnings', 'gaps', 'fundamentals', 'insiders', 'relative-strength'] as const),
    ids: str(s.ids),
    range: str(s.range),
    instrument: str(s.instrument),
    kind: str(s.kind),
  })),
  component: page(() => import('@/features/markets/MarketsPage'), 'MarketsPage'),
});

const testingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/testing',
  validateSearch: optional((s) => ({
    tab: oneOf(s.tab, ['all', 'backtest', 'entry_check', 'refine', 'parameter_search'] as const),
    strategy: str(s.strategy),
  })),
  component: page(() => import('@/features/testing/TestingPage'), 'TestingPage'),
});

const runSetupSearch = optional((s) => ({ strategy: str(s.strategy), instrument: str(s.instrument) }));

const newBacktestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/testing/backtest/new',
  validateSearch: runSetupSearch,
  component: page(() => import('@/features/testing/BacktestSetupPage'), 'BacktestSetupPage'),
});

const newEntryCheckRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/testing/entry-check/new',
  validateSearch: runSetupSearch,
  component: page(() => import('@/features/testing/EntryCheckSetupPage'), 'EntryCheckSetupPage'),
});

const newRefineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/testing/refine/new',
  validateSearch: runSetupSearch,
  component: page(() => import('@/features/testing/RefineSetupPage'), 'RefineSetupPage'),
});

const exitsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/testing/exits',
  component: page(() => import('@/features/testing/ExitStrategiesPage'), 'ExitStrategiesPage'),
});

const backtestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/backtests/$backtestId',
  validateSearch: optional((s) => ({ member: str(s.member) })),
  component: page(() => import('@/features/backtest/BacktestResultPage'), 'BacktestResultPage'),
});

const backtestTradeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/backtests/$backtestId/trades/$tradeIndex',
  component: page(() => import('@/features/backtest/BacktestResultPage'), 'BacktestResultPage'),
});

const entryCheckRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/entry-checks/$checkId',
  component: page(() => import('@/features/entry-check/EntryCheckResultPage'), 'EntryCheckResultPage'),
});

const refineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/refine/$runId',
  component: page(() => import('@/features/refine/RefineResultPage'), 'RefineResultPage'),
});

const newParameterSearchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/testing/parameter-search/new',
  validateSearch: optional((s) => ({
    strategy: str(s.strategy),
    instrument: str(s.instrument),
    backtest: str(s.backtest),
    refine: str(s.refine),
    candidate: str(s.candidate),
  })),
  component: page(() => import('@/features/parameter-search/ParameterSearchSetupPage'), 'ParameterSearchSetupPage'),
});

const parameterSearchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/parameter-searches/$searchId',
  // `measure`: which of the served views the result opens on, so a reload or a shared link reads
  // the same map.
  validateSearch: optional((s) => ({ measure: str(s.measure) })),
  component: page(() => import('@/features/parameter-search/ParameterSearchResultPage'), 'ParameterSearchResultPage'),
});

const whichTestRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/testing/which-test',
  component: page(() => import('@/features/testing/WhichTestPage'), 'WhichTestPage'),
});

const compareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/compare',
  validateSearch: optional((s) => ({ ids: str(s.ids) })),
  component: page(() => import('@/features/compare/ComparePage'), 'ComparePage'),
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/account',
  component: page(() => import('@/features/account/AccountPage'), 'AccountPage'),
});

const plansRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/plans',
  component: page(() => import('@/features/account/PlansPage'), 'PlansPage'),
});

const sharedRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/s/$code',
  component: page(() => import('@/features/strategies/SharedStrategyPage'), 'SharedStrategyPage'),
});

// --- the operator's admin dashboard (docs/admin_dashboard_design.md) -------------------------------
// Its own shell, not the product rail; the API decides who may see it (`require_admin`). The window
// (`?range=`) is validated once on the parent and inherited by every page.
const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  validateSearch: optional(validateAdminSearch),
  component: page(() => import('@/features/admin/AdminShell'), 'AdminShell'),
});

const adminPage = (path: string, loader: () => Promise<Record<string, unknown>>, name: string) =>
  createRoute({ getParentRoute: () => adminRoute, path, component: page(loader, name) });

const adminPages = () => import('@/features/admin/pages/UsersPages');
const adminChildren = [
  adminPage('/', () => import('@/features/admin/pages/OverviewPage'), 'OverviewPage'),
  adminPage('users', adminPages, 'UsersPage'),
  adminPage('users/list', adminPages, 'UserListPage'),
  adminPage('users/$userId', adminPages, 'UserDetailPage'),
  adminPage('revenue', () => import('@/features/admin/pages/RevenuePage'), 'RevenuePage'),
  adminPage('usage', () => import('@/features/admin/pages/UsagePages'), 'UsageFeaturesPage'),
  adminPage('usage/credits', () => import('@/features/admin/pages/UsagePages'), 'UsageCreditsPage'),
  adminPage('usage/strategies', () => import('@/features/admin/pages/UsagePages'), 'UsageStrategiesPage'),
  adminPage('ops', () => import('@/features/admin/pages/OpsPages'), 'OpsJobsPage'),
  adminPage('ops/market', () => import('@/features/admin/pages/OpsPages'), 'OpsMarketPage'),
  adminPage('infra', () => import('@/features/admin/pages/InfraPages'), 'InfraAppsPage'),
  adminPage('infra/db', () => import('@/features/admin/pages/InfraPages'), 'InfraDbPage'),
  adminPage('infra/cost', () => import('@/features/admin/pages/InfraPages'), 'InfraCostPage'),
  adminPage('errors', () => import('@/features/admin/pages/ErrorsPages'), 'ErrorsPage'),
  adminPage('errors/$event', () => import('@/features/admin/pages/ErrorsPages'), 'ErrorDetailPage'),
  adminPage('stores', () => import('@/features/admin/pages/StoresPage'), 'StoresPage'),
];

const routeTree = rootRoute.addChildren([
  indexRoute,
  todayRoute,
  scannerRoute,
  newStrategyRoute,
  editStrategyRoute,
  strategyRoute,
  strategiesRoute,
  templatesRoute,
  watchlistRoute,
  watchlistsRoute,
  alertRoute,
  alertsRoute,
  marketsRoute,
  calendarRoute,
  newBacktestRoute,
  newEntryCheckRoute,
  newRefineRoute,
  exitsRoute,
  testingRoute,
  backtestTradeRoute,
  backtestRoute,
  entryCheckRoute,
  refineRoute,
  newParameterSearchRoute,
  parameterSearchRoute,
  whichTestRoute,
  compareRoute,
  accountRoute,
  plansRoute,
  sharedRoute,
  adminRoute.addChildren(adminChildren),
]);

export const router = createRouter({
  routeTree,
  defaultPendingComponent: () => <LoadingArea />,
  defaultPreload: 'intent',
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
